import ClearIcon from '@mui/icons-material/Clear';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Box,
  Card,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modals } from '../../common/enum';
import Header from '../../components/Header/Header';
import { toggleModals } from '../../services/controllers/common/CommonSlice';
import { useFormulaActivityPaginatedQuery } from '../../services/controllers/formula/FormulaQueries';
import { FormulaActivityAttributes } from '../../services/types/apiType';
import { ActionPaginate } from '../../services/types/common';
import DeleteFormulaActivity from './components/DeleteFormulaActivity';
import FormulaActivityCard from './components/FormulaActivityCard';
import FormulaActivityRow from './components/FormulaActivityRow';
import UpdateFormulaActivity from './components/UpdateFormulaActivity';
import { columns } from './utils';

const sizePerPage = 20;

const FormulaActivity: React.FC = (): React.ReactElement => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [filters, setFilters] = useState({
    page: 1,
    pageSize: sizePerPage,
    searchValue: '',
    date: '',
  });

  const inputRef = useRef(null);

  const { data } = useFormulaActivityPaginatedQuery(filters);
  const activityList = data?.list || [];
  const total = data?.totalCount || 0;

  const handleSubmitSearch = () => {
    if (inputRef.current) {
      setFilters((prev) => ({ ...prev, page: 1, searchValue: (inputRef.current as any).value }));
    }
  };

  const handleChangePage = async (action: ActionPaginate) => {
    setFilters((prev) => ({ ...prev, page: action === ActionPaginate.Next ? prev.page + 1 : prev.page - 1 }));
  };

  const handleClearDate = () => {
    setFilters((prev) => ({ ...prev, date: '' }));
  };

  const handleEditRow = (row: FormulaActivityAttributes) => {
    dispatch(toggleModals({ name: Modals.EDIT_FORMULA_ACTIVITY, data: row }));
  };

  const handleDeleteRow = (row: FormulaActivityAttributes) => {
    dispatch(toggleModals({ name: Modals.DELETE_FORMULA_ACTIVITY, data: row }));
  };

  return (
    <>
      <Header />
      <FormulaActivityPage>
        <CardContent>
          <FilterContainer>
            <SearchContainer>
              <IconButton onClick={handleSubmitSearch}>
                <SearchRoundedIcon />
              </IconButton>
              <InputBase
                inputRef={inputRef}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleSubmitSearch();
                }}
                placeholder="Tìm kiếm..."
              />
            </SearchContainer>
            <DatePickerContainer>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Ngày"
                  format="DD/MM/YYYY"
                  value={filters.date ? dayjs(filters.date) : null}
                  onChange={(date) => setFilters((prev) => ({ ...prev, date: date?.format('YYYY-MM-DD') || '' }))}
                  disableFuture
                />
              </LocalizationProvider>
              {filters.date && (
                <IconButton
                  size="small"
                  onClick={handleClearDate}
                  sx={{ position: 'absolute', right: 40, top: '50%', transform: 'translateY(-50%)' }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </DatePickerContainer>
          </FilterContainer>
          {isDesktop ? (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 232px)' }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column.id} align={column.align} style={{ width: column.width }}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityList.map((row) => (
                    <FormulaActivityRow
                      key={row.id}
                      data={row}
                      onEditRow={() => handleEditRow(row)}
                      onDeleteRow={() => handleDeleteRow(row)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <ListCard>
              {activityList.map((row) => (
                <FormulaActivityCard key={row.id} data={row} />
              ))}
            </ListCard>
          )}
          {!activityList.length ? <Typography align="center">Không có dữ liệu</Typography> : null}
          <TableNavigation>
            <Typography>{`${sizePerPage * (filters.page - 1) || 1}-${
              sizePerPage * filters.page >= total ? total : sizePerPage * filters.page
            } trong ${total}`}</Typography>
            <Box display="flex" alignItems="center">
              <IconButton disabled={filters.page === 1} onClick={() => handleChangePage(ActionPaginate.Previous)}>
                <NavigateBeforeIcon />
              </IconButton>
              Trang {filters.page}
              <IconButton disabled={filters.page === Math.ceil(total / sizePerPage)} onClick={() => handleChangePage(ActionPaginate.Next)}>
                <NavigateNextIcon />
              </IconButton>
            </Box>
          </TableNavigation>
        </CardContent>

        <UpdateFormulaActivity />
        <DeleteFormulaActivity />
      </FormulaActivityPage>
    </>
  );
};

const FilterContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
  marginBottom: 16,
});

const FormulaActivityPage = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: 'calc(100% - 96px)',
  padding: '16px 8px',
}));

const CardContent = styled(Card)(({ theme }) => ({
  maxWidth: 1600,
  margin: 'auto',
  padding: 16,

  [theme.breakpoints.down('md')]: {
    padding: 0,
  },
}));

const TableNavigation = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 16,
  marginTop: 16,
});

const DatePickerContainer = styled('div')({
  position: 'relative',
});

const SearchContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 8,
  [theme.breakpoints.down('md')]: {
    marginBottom: 0,
    height: 56,
    paddingLeft: 16,
  },
}));

const ListCard = styled('div')({
  maxHeight: 'calc(100vh - 208px)',
  overflowY: 'scroll',
});

export default FormulaActivity;
